import { ForeignKey, ExtBase } from "../../../core/db/models";
import { LogisticsProvider } from "./index";
import ValtasiirtoDatabase from "../index";
import OrderItem, { OrderItemLike } from "./orderItem";
import OrderAssignment, { OrderAssignmentLike } from "./orderAssignment";
import { View } from "../../../core/view";

export interface OrderLike extends ExtBase
{
    state: string;
    date: string;
    start_time: string;
    end_time: string;
    required_minutes: string;
    logistics_provider_id: ForeignKey<LogisticsProvider>;
    additional_info: string;
    work_description: string;
    registration_plate: string;
    scheduled: boolean;

    logistics_provider?: LogisticsProvider;
    items?: OrderItemLike[];
    assignments?: OrderAssignmentLike[];
    internal_priority: OrderPriority;
    internal_message: string;
}


export enum OrderPriority {
    
    LOW = 'low',
    NORMAL = 'normal',
    HIGH = 'high',
}

export default class Order implements OrderLike
{
    static sort(orders: OrderLike[]): any {
        orders.sort((a, b) => {
            let priorityA = Order.prioritySortIndex(a);
            let priorityB = Order.prioritySortIndex(b);
            let dueA = Order.startTime(a);
            let dueB = Order.startTime(b);

            if      (priorityA < priorityB)   { return -1; }
            else if (priorityA > priorityB)   {  return 1; }

            if      (dueA < dueB) { return -1; }
            else if (dueA > dueB) { return 1; }
            
            return 0;

        }); 
    }

    state: string;
    date: string;
    start_time: string;
    end_time: string;
    required_minutes: string;
    logistics_provider_id: ForeignKey<LogisticsProvider>;
    additional_info: string;
    work_description: string;
    registration_plate: string;
    scheduled: boolean;
    logistics_provider?: LogisticsProvider;
    items?: OrderItemLike[];
    assignments?: OrderAssignmentLike[];
    ext_id?: string;
    created_at?: string;
    modified_at?: string;
    id?: number;
    internal_priority: OrderPriority;
    internal_message: string;

    public static STATE_NEW = 'new';
    public static STATE_AVAILABLE = 'available';
    public static STATE_ASSIGNED = 'assigned';
    public static STATE_COMPLETED = 'completed';

    public static withAll(db: ValtasiirtoDatabase, targetOrders: OrderLike[] | Dexie.Collection<OrderLike, any>): PromiseLike<OrderLike[]>
    {
        let orders: OrderLike[];

        return new Promise<OrderLike[]>((resolve, reject) =>
        {
            if (Array.isArray(targetOrders))
            {
                return resolve(targetOrders);
            }
            else
            {
                return targetOrders.toArray().then(resolve);
            }
        }).then((orderList) =>
        {   
            orders = orderList;
            // Map order.id and order.logistic_provider to flat arrays
            let orderIds = orders.map(o => o.id);
            let logisticProviderIds = orders.map(o => o.logistics_provider_id) as number[];

            return Promise.all([
                db.LogisticsProvider.where('id').anyOf(logisticProviderIds).toArray(),
                db.OrderItem.where('order_id').anyOf(orderIds).toArray(),
                db.OrderAssignment.where('order_id').anyOf(orderIds).toArray()
            ])
        }).then((results) =>
        {
            let logisticProviders = results[0];
            let orderItems = results[1];
            let assignments = results[2];

            let manufacturerIds = orderItems.map(o => o.manufacturer_id) as number[];
            let parcelTypeIds = orderItems.map(o => o.parcel_type_id) as number[];

            for (let order of orders)
            {
                order.logistics_provider = logisticProviders.find(o => o.id == order.logistics_provider_id);
                order.items = orderItems.filter(o => o.order_id == order.id);
                order.assignments = assignments.filter(o => o.order_id == order.id);

                order.items = [];
                for (let item of orderItems)
                {   
                    if (item.order_id == order.id)
                    {
                        item.order = order;
                        order.items.push(item);
                    }
                }
            }

            return Promise.all([
                OrderItem.withAll(db, orderItems),
                OrderAssignment.withAll(db, assignments)
            ]);
        }).then((results) =>
        {
            return orders;
        });
    }

    public static startTime(order: OrderLike): Date
    {
        return new Date(`${order.date}T${order.start_time}`);
    }

    public static endTime(order: OrderLike): Date
    {
        return new Date(`${order.date}T${order.end_time}`);
    }

    public static prioritySortIndex(order: Order) {
        switch (order.internal_priority || OrderPriority.NORMAL) {
            case OrderPriority.LOW: return 1;
            case OrderPriority.NORMAL: return 0;
            case OrderPriority.HIGH: return -1;
        }
    }

    public static priorityCssClassName(order: OrderLike) {
        return `priority-${order.internal_priority || OrderPriority.NORMAL}`;
    }

    public static priorityLabel(view: View, order: OrderLike) {
        switch (order.internal_priority || OrderPriority.NORMAL) {
            case OrderPriority.LOW: return view.pgettext('Order.priority', 'Low');
            case OrderPriority.NORMAL: return view.pgettext('Order.priority', 'Normal');;
            case OrderPriority.HIGH: return view.pgettext('Order.priority', 'High');;
        }
    }
}